import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import { Button, Input, Logo, Paper, Select } from 'src/components';
import SsoButtons from 'src/components/SsoButtons/SsoButtons';
import { fetchSignupOptions, publicSignup, SignupOption } from 'src/api/functions/signupApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { campaignFromSearch, signupPage, withUtm } from 'src/utils/campaign';
import DemoSignup from './DemoSignup';
import messages from './messages';
import baseStyles from 'src/pages/Onboarding/styles.module.scss';
import formStyles from './DemoSignup.module.scss';
import styles from './Signup.module.scss';

// /signup - create an account with a work email, Google or Microsoft.
//
// Email: POST /api/v1/signup. The backend always answers 202 "check your
// email" (an ICP-rejected visitor must not learn they were filtered), so the
// page shows the same confirmation for every valid submission.
// Google / Microsoft: the SsoButtons carry intent=signup, which is the only
// way an SSO identity is allowed to create an account (the login page's
// buttons never do). The IdP round-trip ends on /login/sso.
// Both paths carry the link's utm_* labels (src/utils/campaign.ts) so the
// account is attributed in folk and Plausible.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function errorCodeOf(r: unknown): string {
  if (!isErrorResponseResult(r as any)) return '';
  try {
    const raw = (r as any).error;
    const err = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return String(err?.error?.code || '').toUpperCase();
  } catch {
    return '';
  }
}

export const Signup = () => {
  const { formatMessage } = useIntl();
  const [industries, setIndustries] = useState<SignupOption[]>([{ value: 'general', label: 'General' }]);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', industry: 'general' });
  const [fieldError, setFieldError] = useState<{ email?: string; name?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSignupOptions().then((r) => {
      if (cancelled || isErrorResponseResult(r) || !r.industries?.length) return;
      setIndustries(r.industries);
      if (r.default_industry) setForm((f) => ({ ...f, industry: r.default_industry }));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setFormError(null);
    const email = form.email.trim().toLowerCase();
    const errors: { email?: string; name?: string } = {};
    if (!EMAIL_RE.test(email)) errors.email = formatMessage(messages.emailInvalid);
    if (!form.firstName.trim() || !form.lastName.trim()) errors.name = formatMessage(messages.nameRequired);
    setFieldError(errors);
    if (errors.email || errors.name) return;

    setSubmitting(true);
    const r = await publicSignup({
      email,
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      industry: form.industry,
      source: 'dashboard_signup',
      campaign: campaignFromSearch(window.location.search),
      page: signupPage(),
    });
    setSubmitting(false);
    if (isErrorResponseResult(r)) {
      const code = errorCodeOf(r);
      if (code === 'INVALID_EMAIL') {
        setFieldError({ email: formatMessage(messages.emailInvalid) });
      } else if (r.status === 429) {
        setFormError(formatMessage(messages.tooMany));
      } else {
        setFormError(formatMessage(messages.genericError));
      }
      return;
    }
    setSent(email);
  };

  if (sent) {
    return (
      <main className={baseStyles.onboarding}>
        <header>
          <Logo />
        </header>
        <section className={baseStyles.container}>
          <Paper className={baseStyles.content} data-test="signup-sent">
            <h1>
              <FormattedMessage {...messages.sentTitle} />
            </h1>
            <p>
              <FormattedMessage {...messages.sentBody} values={{ email: <strong>{sent}</strong> }} />
            </p>
            <p className={styles.finePrint}>
              <FormattedMessage {...messages.sentHint} />
            </p>
            <p className={styles.switch}>
              <Link to="/login">
                <FormattedMessage {...messages.backToSignIn} />
              </Link>
            </p>
          </Paper>
        </section>
      </main>
    );
  }

  return (
    <main className={baseStyles.onboarding}>
      <header>
        <Logo />
      </header>
      <section className={baseStyles.container}>
        <Paper className={baseStyles.content} data-test="signup">
          <h1>
            <FormattedMessage {...messages.title} values={{ brand: <span>FileSpin</span> }} />
          </h1>
          <p>
            <FormattedMessage {...messages.intro} />
          </p>

          <div className={styles.sso}>
            <SsoButtons
              intent="signup"
              returnTo="/assets"
              showDivider={false}
              afterLabel={<FormattedMessage {...messages.orWithEmail} />}
            />
          </div>

          <form className={formStyles.form} onSubmit={submit} noValidate>
            <Input
              label={formatMessage(messages.workEmail)}
              variant="secondary"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              error={fieldError.email}
            />
            <div className={formStyles.nameRow}>
              <Input
                label={formatMessage(messages.firstName)}
                variant="secondary"
                value={form.firstName}
                onChange={(v) => setForm({ ...form, firstName: v })}
              />
              <Input
                label={formatMessage(messages.lastName)}
                variant="secondary"
                value={form.lastName}
                onChange={(v) => setForm({ ...form, lastName: v })}
              />
            </div>
            {fieldError.name && <p className={formStyles.error}>{fieldError.name}</p>}
            <Select
              label={formatMessage(messages.industry)}
              value={form.industry}
              onChange={(v) => setForm({ ...form, industry: v })}
              options={industries}
            />
            {formError && <p className={formStyles.error}>{formError}</p>}
            <Button type="submit" loading={submitting}>
              <FormattedMessage {...messages.createAccount} />
            </Button>
            <p className={styles.finePrint}>
              <FormattedMessage {...messages.termsNote} />{' '}
              <a href="https://filespin.io/terms">Terms of Service</a> &middot;{' '}
              <a href="https://filespin.io/privacy/">Privacy Policy</a>
            </p>
          </form>

          <p className={styles.switch}>
            <FormattedMessage {...messages.haveAccount} />{' '}
            <Link to={withUtm('/login', window.location.search)}>
              <FormattedMessage {...messages.signIn} />
            </Link>
          </p>
        </Paper>
      </section>
    </main>
  );
};

// /signup serves two flows: the demo-claim instant onboarding when the link
// carries `demo_run_id` + `t`, and the general signup page otherwise.
export const SignupRoute = () => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  if (params.get('demo_run_id') || params.get('t')) {
    return <DemoSignup />;
  }
  return <Signup />;
};

export default Signup;
